<!--
	The several-assignments pane: what the ledger's checkboxes selected, as one
	list rather than one record's details.

	It reads and never writes. The one thing it can start is a Route holding these
	assignments, and that happens in the Route tool on the right rail — the door
	here is the hint, the form is where the technician and window are stated. A
	selection that cannot become one Route (another technician's work, a row
	already on a route, work that is no longer draft) says why instead of opening
	a form that would refuse it.
-->
<template>
	<div>
		<div class="d-flex align-items-center gap-2 mb-2">
			<span class="fw-semibold">{{ assignments.length }} Assignments</span>
			<button
				class="btn btn-sm btn-outline-secondary ms-auto"
				@click="emit('clear')"
			>
				<i class="bi bi-x-lg"></i>
			</button>
		</div>

		<div v-for="row in assignments" :key="row.id" class="border-bottom py-1">
			<div class="d-flex gap-2 align-items-baseline">
				<span class="badge" :class="kindBadgeClass(row.kind)">
					{{ kindLabel(row.kind) }}
				</span>
				<span class="fw-semibold text-truncate">#{{ row.id }}</span>
				<span class="ms-auto small text-body-secondary">
					{{ technicianName(row.technicianId) }}
				</span>
			</div>
			<div class="small text-muted text-truncate">
				{{ targetLabel(row, sites) }} · {{ stateLabel(row.state) }}
			</div>
		</div>

		<div class="mt-2">
			<button
				v-if="routeBlock === null"
				type="button"
				class="btn btn-link btn-sm p-0 d-block text-start"
				@click="emit('create-route', assignmentIds)"
			>
				Create route…
			</button>
			<p class="small text-body-secondary mt-1 mb-0">
				<template v-if="routeBlock === null">
					Starts the New route tool with these assignments, in this order. The
					technician is theirs, and the form takes the window.
				</template>
				<template v-else>{{ routeBlock }}</template>
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { MapReviewSiteRow, SyncAssignment, User } from "@/type/api";

import { kindBadgeClass, kindLabel, stateLabel, targetLabel } from "./wire";

const props = defineProps<{
	assignments: SyncAssignment[];
	users: User[];
	sites: MapReviewSiteRow[];
}>();

const emit = defineEmits<{
	(e: "clear"): void;
	(e: "create-route", assignmentIds: number[]): void;
}>();

/** The ids in the order the pane lists them: a Route's membership keeps the
 * order it is given, so this is the sequence the operator is looking at. */
const assignmentIds = computed(() => props.assignments.map((row) => row.id));

/** Why these assignments cannot be one Route, or null when they can. A Route
 * carries one technician (§8 Rule 2), holds work no other Route already holds
 * (§8 Rule 3), and the Route form offers draft work, so a selection outside
 * that would be carried into a form that could only refuse it. */
const routeBlock = computed<string | null>(() => {
	const [first] = props.assignments;
	if (first === undefined) return "Select assignments to start a route.";
	const routed = props.assignments.find((row) => row.routeId !== null);
	if (routed) {
		return `#${routed.id} is already on a route; remove it from that route first.`;
	}
	const settled = props.assignments.find((row) => row.state !== "draft");
	if (settled) {
		return `#${settled.id} is ${stateLabel(settled.state).toLowerCase()}; the Route form starts from draft assignments.`;
	}
	const split = props.assignments.find(
		(row) => row.technicianId !== first.technicianId,
	);
	if (split) {
		return "These assignments belong to more than one technician; a Route binds exactly one.";
	}
	const technician = props.users.find((row) => row.id === first.technicianId);
	if (!technician || !technician.is_active) {
		return `Technician #${first.technicianId} is not an active member, and a Route binds a live technician.`;
	}
	return null;
});

function technicianName(technicianId: number): string {
	const user = props.users.find((row) => row.id === technicianId);
	return user
		? user.display_name || user.username
		: `Technician #${technicianId}`;
}
</script>
